import { Box, Container, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { companiesApi } from 'src/api/companiesApi';
import SubHeader from 'src/components/SubHeader';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { companiesColumns } from 'src/constants/columns';
import useGenericStyles from 'src/styles/useGenericStyles';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import CreateClientWizard from './create-client-wizard/CreateClientWizard';

export default function Companies() {
    const theme = useTheme();
    const classes = useGenericStyles();
    const { userData } = useUserProfile();
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState({});
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);

    const fetchChildCompanies = async (currentPage = 0, currentSearch = '') => {
        setLoading(true);
        try {
            const response = await companiesApi.getChildCompaniesByParentId({
                parentId: userData?.organization?.id,
                page: currentPage,
                pageSize,
                search: currentSearch
            });
            if (response?.data) {
                setData(response.data?.data);
                setTotalCount(response.data?.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching child companies:', error);
            setData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPage(0);
    };

    const handlePage = (temp: number) => {
        setPage(temp);
    };
    const handlePageSize = (temp: number) => {
        setPageSize(temp);
    };

    const handleSort = (col: any) => setSort(col);

    const handleRowClick = (row) => {
        navigate(`/clients/${row.id}`);
    };

    useEffect(() => {
        if (userData?.organization?.id) {
            fetchChildCompanies(page, search);
        }
    }, [page, search, pageSize, userData]);

    return (
        <>
            <Helmet>
                <title>IT22 ERP | Clients</title>
            </Helmet>
            <SubHeader title="Clients" />
            <Container maxWidth={false} className={classes.container}>
                <Box
                    sx={{
                        marginBottom: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <SearchBar
                        value={search}
                        onSearch={handleSearch}
                        placeholderData="Search..."
                    />
                    <CustomButton2
                        backgroundColor={theme.palette.primary.main}
                        className={classes.button}
                        onClick={() => setIsCreateClientOpen(true)}>
                        <AddIcon
                            sx={{
                                marginRight: '5px',
                            }}
                        />
                        Create New Client Company
                    </CustomButton2>
                </Box>

                <CustomizedTable
                    heading="All Clients"
                    loading={loading}
                    columns={companiesColumns}
                    page={page}
                    handleOnClick={handleRowClick}
                    limit={pageSize}
                    handleLimit={handlePageSize}
                    handlePage={handlePage}
                    data={data}
                    totalRecords={totalCount}
                    handleSort={handleSort}
                    sort={sort}
                    navigateToKey
                    selectRowData
                    customizableColumns
                />
            </Container>
            <CreateClientWizard
                open={isCreateClientOpen}
                onClose={() => setIsCreateClientOpen(false)}
                onCreated={() => fetchChildCompanies(page, search)}
            />
        </>
    );
}
